import React, { useState } from "react";
import "./TechAn.css";
import axios from "axios";
import Select from "react-select";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-moment";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function TechAn() {
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [dates, setDates] = useState([]);
  const [strategiesData, setStrategiesData] = useState({});
  const [period, setPeriod] = useState("2m");
  const [formData, setFormData] = useState({
    ticker: "",
    strategies: [],
  });

  const selectoptions = [
    { value: "rsi", label: "RSI" },
    { value: "macd", label: "MACD" },
    { value: "bollingerbands", label: "BOLLINGER BANDS" },
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "rgb(26, 26, 26)",
      borderRadius: "5px",
      minWidth: "200px",
      borderColor: "#6e6e6e",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#6e6e6e",
      color: "black",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "rgb(196, 196, 196)",
      ":hover": {
        backgroundColor: "darkred",
        color: "white",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "rgb(26, 26, 26)",
      borderRadius: "5px",
      borderColor: "white",
      zIndex: 100,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "250px",
      overflowY: "auto",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "white" : "black",
      color: state.isFocused ? "black" : "white",
      padding: "10px",
      cursor: "pointer",
    }),
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.toUpperCase(),
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const strategiesQuery = formData.strategies
      .map((strategy) => `strategies=${strategy}`)
      .join("&");

    const apiUrl = `https://api.stockastix.com/backtrading/backtest/?ticker=${formData.ticker}&${strategiesQuery}`;

    try {
      const response = await axios.get(apiUrl);
      const { prices, dates, strategies_data } = response.data;

      setPrices(prices);
      setDates(dates);
      setStrategiesData(strategies_data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = () => {
    const datasets = [];

    const PERIOD_MAP = {
      "2m": { amount: 2, unit: "months" },
      "3m": { amount: 3, unit: "months" },
      "6m": { amount: 6, unit: "months" },
      "1y": { amount: 1, unit: "years" },
      "3y": { amount: 3, unit: "years" },
    };

    const { amount, unit } = PERIOD_MAP[period] || PERIOD_MAP["2m"];
    const cutoffDate = moment().subtract(amount, unit);

    // filter indices where date >= cutoffDate
    const filteredIndices = dates
      .map((d, idx) =>
        moment(d).isSameOrAfter(cutoffDate, "day") ? idx : null
      )
      .filter((idx) => idx !== null);

    const filteredDates = filteredIndices.map((i) => dates[i]);
    const filteredPrices = filteredIndices.map((i) => prices[i]);

    datasets.push({
      label: "Prices",
      data: filteredPrices,
      borderColor: "rgba(75, 192, 192, 1)",
      backgroundColor: "rgba(75, 192, 192, 0.2)",
      borderWidth: 2.5,
      fill: false,
      yAxisID: "y1",
      pointRadius: 0,
    });

    for (const strategy in strategiesData) {
      const strategyInfo = strategiesData[strategy];

      const colorMap = {
        macd: "rgba(153, 102, 255, 1)",
        rsi: "rgba(255, 206, 86, 1)",
        bollingerbands: "rgba(75, 192, 192, 1)",
      };

      const backgroundColorMap = {
        macd: "rgba(153, 102, 255, 0.2)",
        rsi: "rgba(255, 206, 86, 0.2)",
        bollingerbands: "rgba(75, 192, 192, 0.2)",
      };

      const maybeFilter = (arr) =>
        arr ? filteredIndices.map((i) => arr[i]) : null;

      if (strategyInfo.macd_values) {
        datasets.push({
          label: "MACD",
          data: maybeFilter(strategyInfo.macd_values),
          borderColor: colorMap[strategy],
          backgroundColor: backgroundColorMap[strategy],
          borderWidth: 1,
          fill: false,
          yAxisID: "y2",
          pointRadius: 0,
        });
      }

      if (strategyInfo.signal_values) {
        datasets.push({
          label: "MACD Signal",
          data: maybeFilter(strategyInfo.signal_values),
          borderColor: "rgba(255, 159, 64, 1)",
          backgroundColor: "rgba(255, 159, 64, 0.2)",
          borderWidth: 1,
          fill: false,
          yAxisID: "y2",
          pointRadius: 0,
        });
      }

      if (strategyInfo.rsi_values) {
        datasets.push({
          label: "RSI",
          data: maybeFilter(strategyInfo.rsi_values),
          borderColor: colorMap[strategy],
          backgroundColor: backgroundColorMap[strategy],
          borderWidth: 1,
          fill: false,
          yAxisID: "y2",
          pointRadius: 0,
        });
      }

      if (strategy === "bollingerbands") {
        if (strategyInfo.upperband_values && strategyInfo.lowerband_values) {
          datasets.push({
            label: "Bollinger Lower Band",
            data: maybeFilter(strategyInfo.lowerband_values),
            borderColor: "rgba(255, 0, 0, 1)",
            backgroundColor: "rgba(255, 0, 0, 0.1)",
            borderWidth: 1,
            fill: false,
            yAxisID: "y1",
            pointRadius: 0,
          });

          datasets.push({
            label: "Bollinger Upper Band",
            data: maybeFilter(strategyInfo.upperband_values),
            borderColor: "rgba(255, 0, 0, 1)",
            backgroundColor: "rgba(255, 0, 0, 0.1)",
            borderWidth: 1,
            fill: "-1",
            yAxisID: "y1",
            pointRadius: 0,
          });
        }
      }
    }

    return {
      labels: filteredDates,
      datasets: datasets,
    };
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: true,
    plugins: {
      title: {
        display: true,
        text: "Stock Prices vs Strategies",
        color: "white",
      },
      legend: {
        labels: {
          color: "white",
        },
      },
      tooltip: {
        backgroundColor: "rgb(51, 51, 51, 0.7)",
        titleColor: "rgb(256, 256, 256)",
        bodyColor: "rgb(256, 256, 256)",
        borderColor: "rgb(256, 256, 256, 0.1)",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
        },
        title: {
          display: true,
          text: "Date",
          color: "white",
        },
        ticks: { color: "white", maxTicksLimit: 25 },
        grid: { color: "#444444" },
      },
      y1: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Price",
          color: "white",
        },
        ticks: { color: "white" },
      },
      y2: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text: "Indicator Value",
          color: "white",
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: { color: "white" },
      },
    },
  };

  return (
    <div className="container">
      <div className="text-center my-3">
        <h2>Technical Analysis</h2>
      </div>

      {loading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="form">
        <div className="form-row">
          <div className="form-group">
            <label>Ticker Symbol:</label>
            <input
              type="text"
              name="ticker"
              className="ticker-inputt"
              placeholder="Ticker"
              value={formData.ticker}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label>Choose Strategy:</label>
            <Select
              isMulti
              name="strategies"
              styles={customStyles}
              options={selectoptions}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectoptions.filter((option) =>
                formData.strategies.includes(option.value)
              )}
              onChange={(selectedOptions) => {
                if (selectedOptions.length > 3) {
                  alert("You can only select up to 3 strategies.");
                  return;
                }
                setFormData({
                  ...formData,
                  strategies: selectedOptions.map((option) => option.value),
                });
              }}
            />
          </div>
          <div className="form-row">
            <button type="submit" className="submit-button">
              Submit
            </button>
          </div>
        </div>
      </form>

      {!loading && prices.length > 0 && (
        <div className="chart-container">
          <div className="period-buttons">
            {["2m", "3m", "6m", "1y", "3y"].map((option, index) => (
              <span
                key={option}
                onClick={() => setPeriod(option)}
                style={{
                  backgroundColor: period === option ? "#ff9800" : "#333",
                  color: period === option ? "#000" : "#fff",
                }}
              >
                {option}
              </span>
            ))}
          </div>
          <Line
            data={generateChartData()}
            options={chartOptions}
            height={100}
          />
        </div>
      )}
    </div>
  );
}

export default TechAn;
